import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from "motion/react";
import { Link } from 'react-router-dom';
import { useTheme } from '@/theme/hooks/useTheme';
import { useAuth } from '@/hooks/useAuth';
import CartIconBadge from './CartIconBadge';
import ProfileDropdown from './ProfileDropdown';
import ShareModal from './ShareModal';
import { BellIcon, ShareIcon } from '@/components/icons';
import { MenuToggleIcon } from './ui/menu-toggle-icon';
import { likeService } from '@/features/likes/services/likeService';
import { authService } from '@/features/user/services/authService';
import type { LikeableEntityType } from '@/features/likes/types/likeTypes';

// SVG Icons
const HeartIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5 transition-transform duration-300 group-hover:scale-110">
    <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z"></path>
  </svg>
);

const SunIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5">
    <circle cx="12" cy="12" r="4"></circle>
    <path d="M12 2v2"></path>
    <path d="M12 20v2"></path>
    <path d="m4.93 4.93 1.41 1.41"></path>
    <path d="m17.66 17.66 1.41 1.41"></path>
    <path d="M2 12h2"></path>
    <path d="M20 12h2"></path>
    <path d="m6.34 17.66-1.41 1.41"></path>
    <path d="m19.07 4.93-1.41 1.41"></path>
  </svg>
);

const MoonIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5">
    <path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z"></path>
  </svg>
);

const UserIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5">
    <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"></path>
    <circle cx="12" cy="7" r="4"></circle>
  </svg>
);


interface HeaderProps {
  brandName?: string;
  brandLink?: string;
  navigationLinks?: Array<{
    label: string;
    href: string;
  }>;
  variant?: 'main' | 'club' | 'event' | 'artist';
  className?: string;
  // Entity-based action buttons
  entityId?: string;
  entityType?: LikeableEntityType;
  showShare?: boolean;
  showBell?: boolean;
  showHamburger?: boolean;
}

const Header: React.FC<HeaderProps> = ({ 
  brandName = 'NightSync',
  brandLink = '/',
  navigationLinks = [
    { label: 'Venues', href: '#venues' },
    { label: 'Events', href: '#events' },
    { label: 'VIP Access', href: '#' }
  ],
  variant = 'main',
  className = '',
  entityId,
  entityType = 'businesses',
  showShare = false,
  showBell = false,
  showHamburger = true,
}) => {
  const { theme, toggleTheme } = useTheme();
  const { isAuthenticated, isGuest, isLoading: authLoading, user, refetch: refetchAuth } = useAuth();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [isLiked, setIsLiked] = useState(false);
  const [isLikeLoading, setIsLikeLoading] = useState(false);
  const [isBellActive, setIsBellActive] = useState(false);
  const [isBellAnimating, setIsBellAnimating] = useState(false);
  
  // Show profile dropdown only if authenticated AND not a guest user (and not loading)
  // Double check: user?.is_guest to ensure we catch the guest status
  const shouldShowProfile = !authLoading && isAuthenticated && !isGuest && !user?.is_guest;

  // Check initial like status on mount
  useEffect(() => {
    const checkLikeStatus = async () => {
      if (!entityId || !entityType || !isAuthenticated) return;
      
      try {
        const response = await likeService.checkLikes({
          entity_type: entityType,
          entity_ids: [entityId],
        });
        
        if (response.liked_map && response.liked_map[entityId]) {
          setIsLiked(true);
        }
      } catch (error) {
        console.error('Failed to check like status:', error);
      }
    };

    checkLikeStatus();
  }, [entityId, entityType, isAuthenticated]);

  // Handle like button click
  const handleLikeClick = async () => {
    if (isLikeLoading || !entityId) return;

    const newLikedState = !isLiked;
    setIsLiked(newLikedState);

    // If not authenticated, register as guest first
    if (!isAuthenticated) {
      try {
        setIsLikeLoading(true);
        await authService.registerGuest();
        await refetchAuth();
      } catch (error) {
        console.error('Failed to register guest:', error);
        setIsLiked(isLiked); // Revert
        setIsLikeLoading(false);
        return;
      } finally {
        setIsLikeLoading(false);
      }
    }

    // Toggle like
    try {
      await likeService.toggleLike({
        entity_id: entityId,
        entity_type: entityType,
      });
    } catch (error) {
      console.error('Failed to toggle like:', error);
      setIsLiked(!newLikedState); // Revert on error
    }
  };

  const containerVariants = {
    hidden: { opacity: 0, y: -20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        staggerChildren: 0.1,
        delayChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: -10 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
      },
    },
  };

  const buttonVariants = {
    hover: {
      scale: 1.05,
      transition: {
        duration: 0.2,
      },
    },
    tap: {
      scale: 0.95,
    },
  };

  // Dynamic styling based on variant
  const headerClasses = variant === 'club' 
    ? "fixed top-0 w-full bg-black/40 backdrop-blur-xl border-b border-gray-800/30 z-40"
    : "fixed top-0 w-full bg-white/80 dark:bg-black/80 backdrop-blur-xl border-b border-gray-200/50 dark:border-gray-800/50 z-40";

  const brandClasses = variant === 'club'
    ? "text-2xl font-serif font-medium tracking-tight text-white hover:text-blue-400 transition-colors"
    : "text-2xl font-serif font-medium tracking-tight text-gray-900 dark:text-white hover:text-blue-600 dark:hover:text-blue-400 transition-colors";

  return (
    <>
    <motion.header 
      className={`${headerClasses} ${className}`}
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      <nav className="max-w-8xl mx-auto flex items-center justify-between px-4 md:px-8 py-6">
        <motion.div 
          className="flex items-center gap-12"
          variants={itemVariants}
        >
          <Link to={brandLink} className={brandClasses}>
            {brandName}
          </Link>
          <div className={`hidden md:flex items-center gap-8 ${
            variant === 'club' || variant === 'event' || variant === 'artist'
              ? 'text-sm font-medium text-gray-300'
              : 'text-sm font-medium text-gray-600 dark:text-gray-300'
          }`}>
            {navigationLinks.map((link, index) => (
              <a key={index} href={link.href} className="relative group">
                <span className={`transition-colors duration-300 ${
                  variant === 'club' || variant === 'event' || variant === 'artist'
                    ? 'hover:text-white'
                    : 'hover:text-gray-900 dark:hover:text-white'
                }`}>
                  {link.label}
                </span>
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-blue-500 transition-all duration-300 group-hover:w-full"></span>
              </a>
            ))}
          </div>
        </motion.div>
        
        {/* Desktop Navigation - Simplified: Profile, Like, Cart, Menu */}
        <motion.div 
          className={`hidden md:flex items-center gap-4 ${variant === 'club' || variant === 'event' || variant === 'artist' ? 'text-white' : 'text-gray-900 dark:text-white'}`}
          variants={itemVariants}
        >
          {shouldShowProfile ? (
            <ProfileDropdown variant={variant === 'event' || variant === 'artist' ? 'club' : variant} />
          ) : (
            <Link to="/login">
              <motion.button 
                className={`text-sm font-medium transition-colors hover:cursor-pointer ${
                  variant === 'club' || variant === 'event' || variant === 'artist'
                    ? 'hover:text-blue-400' 
                    : 'hover:text-blue-600 dark:hover:text-blue-400'
                }`}
                variants={buttonVariants}
                whileHover="hover"
                whileTap="tap"
              >
                Log In
              </motion.button>
            </Link>
          )}
          
          {/* Entity-based Like Button */}
          {entityId && entityType ? (
            <motion.button 
              onClick={handleLikeClick}
              disabled={isLikeLoading}
              className="relative group p-1"
              variants={buttonVariants}
              whileHover="hover"
              whileTap="tap"
            >
              <motion.div
                animate={{ scale: isLiked ? [1, 1.3, 1] : 1 }}
                transition={{ duration: 0.3 }}
              >
                {isLiked ? (
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="#ef4444" stroke="#ef4444" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="transition-transform duration-300 group-hover:scale-110">
                    <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z"></path>
                  </svg>
                ) : (
                  <HeartIcon />
                )}
              </motion.div>
            </motion.button>
          ) : (
            <motion.button 
              className="relative group"
              variants={buttonVariants}
              whileHover="hover"
              whileTap="tap"
            >
              <HeartIcon />
            </motion.button>
          )}
          
          {/* Bell Button - visible in header when showBell is true */}
          {showBell && (
            <motion.button 
              onClick={() => {
                setIsBellActive(!isBellActive);
                setIsBellAnimating(true);
                setTimeout(() => setIsBellAnimating(false), 600);
              }}
              className={`relative group p-1 ${
                isBellActive
                  ? 'text-yellow-400'
                  : ''
              }`}
              variants={buttonVariants}
              whileHover="hover"
              whileTap="tap"
            >
              <motion.div
                animate={isBellAnimating ? { rotate: [0, 15, -15, 10, -10, 5, -5, 0] } : {}}
                transition={{ duration: 0.6 }}
              >
                <BellIcon className={`w-5 h-5 ${isBellActive ? 'fill-current' : ''}`} />
              </motion.div>
            </motion.button>
          )}
          
          <CartIconBadge variant={variant} />
          
          {/* Theme Toggle (always visible when no hamburger) */}
          {!showHamburger && (
            <button
              onClick={toggleTheme}
              className="relative group p-2 rounded-lg hover:bg-gray-100/10 transition-colors"
              aria-label="Toggle theme"
            >
              {theme === 'dark' ? <SunIcon /> : <MoonIcon />}
            </button>
          )}
          
          {/* Hamburger Menu Button for Desktop */}
          {showHamburger && (
            <button 
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="relative group p-2 rounded-lg hover:bg-gray-100/10 transition-colors"
              aria-label="Toggle menu"
            >
              <MenuToggleIcon open={isMobileMenuOpen} className="w-6 h-6" duration={400} />
            </button>
          )}
        </motion.div>

        {/* Mobile Navigation - Same as desktop now */}
        <motion.div 
          className={`md:hidden flex items-center gap-4 ${variant === 'club' ? 'text-white' : 'text-gray-900 dark:text-white'}`}
          variants={itemVariants}
        >
          {/* Like button */}
          {entityId && entityType ? (
            <motion.button 
              onClick={handleLikeClick}
              disabled={isLikeLoading}
              className="relative group"
              variants={buttonVariants}
              whileHover="hover"
              whileTap="tap"
            >
              {isLiked ? (
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="#ef4444" stroke="#ef4444" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z"></path>
                </svg>
              ) : (
                <HeartIcon />
              )}
            </motion.button>
          ) : (
            <motion.button 
              className="relative group"
              variants={buttonVariants}
              whileHover="hover"
              whileTap="tap"
            >
              <HeartIcon />
            </motion.button>
          )}
          
          {/* Bell Button - Mobile */}
          {showBell && (
            <motion.button 
              onClick={() => {
                setIsBellActive(!isBellActive);
                setIsBellAnimating(true);
                setTimeout(() => setIsBellAnimating(false), 600);
              }}
              className={`relative group p-1 ${
                isBellActive
                  ? 'text-yellow-400'
                  : ''
              }`}
              variants={buttonVariants}
              whileHover="hover"
              whileTap="tap"
            >
              <motion.div
                animate={isBellAnimating ? { rotate: [0, 15, -15, 10, -10, 5, -5, 0] } : {}}
                transition={{ duration: 0.6 }}
              >
                <BellIcon className={`w-5 h-5 ${isBellActive ? 'fill-current' : ''}`} />
              </motion.div>
            </motion.button>
          )}
          
          <CartIconBadge variant={variant} />
          
          {/* Theme Toggle (always visible when no hamburger) */}
          {!showHamburger && (
            <button
              onClick={toggleTheme}
              className="relative group p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              aria-label="Toggle theme"
            >
              {theme === 'dark' ? <SunIcon /> : <MoonIcon />}
            </button>
          )}
          
          {/* Hamburger Menu Button */}
          {showHamburger && (
            <button 
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="relative group p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              aria-label="Toggle mobile menu"
            >
              <MenuToggleIcon open={isMobileMenuOpen} className="w-6 h-6" duration={400} />
            </button>
          )}
        </motion.div>
      </nav>

      {/* Backdrop - subtle, no harsh overlay */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={() => setIsMobileMenuOpen(false)}
            className="fixed inset-0 z-[40]"
          />
        )}
      </AnimatePresence>

      {/* Dropdown Menu - clean glass effect */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
            className={`fixed top-24 md:top-26 right-2 w-56 rounded-xl z-[50] shadow-xl overflow-hidden border ${
              variant === 'club' || variant === 'event' || variant === 'artist'
                ? 'bg-black/40 backdrop-blur-xl border-gray-800/30' 
                : 'bg-white/40 dark:bg-black/40 backdrop-blur-xl border-gray-200/30 dark:border-gray-800/30'
            }`}
          >
            <div className="p-2">
              {/* Action Buttons */}
              <motion.ul
                initial="closed"
                animate="open"
                variants={{
                  open: {
                    transition: {
                      staggerChildren: 0.08,
                      delayChildren: 0.1,
                    },
                  },
                  closed: {
                    transition: {
                      staggerChildren: 0.05,
                      staggerDirection: -1,
                    },
                  },
                }}
                className="space-y-1"
              >
                {/* Theme Toggle */}
                <motion.li
                  variants={{
                    open: { opacity: 1, x: 0 },
                    closed: { opacity: 0, x: 20 },
                  }}
                >
                  <button 
                    onClick={toggleTheme}
                    className={`w-full flex items-center gap-3 px-4 py-3 text-base font-medium rounded-lg hover:bg-white/10 transition-colors duration-200 ${
                      variant === 'club' || variant === 'event' || variant === 'artist'
                        ? 'text-white'
                        : 'text-gray-900 dark:text-white'
                    }`}
                  >
                    {theme === 'light' ? <MoonIcon /> : <SunIcon />}
                    <span>{theme === 'light' ? 'Dark Mode' : 'Light Mode'}</span>
                  </button>
                </motion.li>
                
                {/* Share Button */}
                {showShare && (
                  <motion.li
                    variants={{
                      open: { opacity: 1, x: 0 },
                      closed: { opacity: 0, x: 20 },
                    }}
                  >
                    <button 
                      onClick={() => {
                        setIsShareModalOpen(true);
                        setIsMobileMenuOpen(false);
                      }}
                      className={`w-full flex items-center gap-3 px-4 py-3 text-base font-medium rounded-lg hover:bg-white/10 transition-colors duration-200 ${
                        variant === 'club' || variant === 'event' || variant === 'artist'
                          ? 'text-white'
                          : 'text-gray-900 dark:text-white'
                      }`}
                    >
                      <ShareIcon className="w-5 h-5" />
                      <span>Share</span>
                    </button>
                  </motion.li>
                )}
              </motion.ul>
              
              {/* Profile/Login - Mobile Only */}
              <div className="md:hidden mt-2 pt-2 border-t border-gray-700/50 space-y-1">
                {shouldShowProfile ? (
                  <>
                    {/* Profile Link */}
                    <Link 
                      to="/profile" 
                      onClick={() => setIsMobileMenuOpen(false)}
                      className={`flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-white/10 transition-colors duration-200 ${
                        variant === 'club' || variant === 'event' || variant === 'artist'
                          ? 'text-white'
                          : 'text-gray-900 dark:text-white'
                      }`}
                    >
                      <UserIcon />
                      <span className="text-base font-medium">Profile</span>
                    </Link>
                    
                    {/* Logout Button */}
                    <button 
                      onClick={() => {
                        setIsMobileMenuOpen(false);
                        // Navigate to login which will trigger logout
                        window.location.href = '/login?logout=true';
                      }}
                      className={`w-full flex items-center gap-3 px-4 py-3 text-base font-medium rounded-lg hover:bg-white/10 transition-colors duration-200 ${
                        variant === 'club' || variant === 'event' || variant === 'artist'
                          ? 'text-red-400 hover:text-red-300'
                          : 'text-red-500 dark:text-red-400 hover:text-red-600 dark:hover:text-red-300'
                      }`}
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                      </svg>
                      <span>Logout</span>
                    </button>
                  </>
                ) : (
                  <Link to="/login" onClick={() => setIsMobileMenuOpen(false)}>
                    <button className={`w-full flex items-center gap-3 px-4 py-3 text-base font-medium rounded-lg transition-colors duration-200 ${
                      variant === 'club' || variant === 'event' || variant === 'artist'
                        ? 'text-white hover:bg-white/10'
                        : 'text-gray-900 dark:text-white hover:bg-white/10'
                    }`}>
                      <UserIcon />
                      <span>Log In</span>
                    </button>
                  </Link>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>

      {/* Share Modal */}
      {showShare && (
        <ShareModal
          isOpen={isShareModalOpen}
          onClose={() => setIsShareModalOpen(false)}
          entityName={brandName}
        />
      )}
    </>
  );
};

export default Header;
